import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik, useFormikContext } from 'formik';
import { cloneDeep, get } from 'lodash';

import { useCliveConfigOptions } from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationContext';

import CardDialog from '../../../../../CardDialog';
import DialogFooterActions from '../../../../../core/DialogFooterActions/index';
import { incidentTypesAndSubTypesDialogSchema } from '../../../fnolConfigurationValidationSchema';

import CategoriesSection from './CategoriesSection';
import IncidentTypesSubTypesConfigurationArray from './IncidentTypesSubTypesConfigurationArray';

const IncidentTypesAndSubTypesDialogInner = ({ onClose }) => {
  const { handleSubmit } = useFormikContext();

  const { showCliveConfigOptions } = useCliveConfigOptions();

  return (
    <CardDialog
      isDialog
      onClose={onClose}
      open
      title="Incident Types And SubTypes"
      maxWidth={showCliveConfigOptions ? 'lg' : 'md'}
      fullWidth
      footerActions={<DialogFooterActions onClickSecondary={onClose} onClickPrimary={handleSubmit} />}
    >
      <CategoriesSection />
      <IncidentTypesSubTypesConfigurationArray />
    </CardDialog>
  );
};

IncidentTypesAndSubTypesDialogInner.propTypes = {
  onClose: PropTypes.func.isRequired,
};

function normalizeIncidentTypesActiveDefaults(values) {
  const normalized = cloneDeep(values);
  const types = get(normalized, 'configuration.incident_details.types', []);

  const normalizedTypes = types.map((type) => {
    const subTypes = get(type, 'sub_types', []);
    return {
      ...type,
      active: type?.active !== false,
      sub_types: subTypes.map((subType) => ({
        ...subType,
        active: subType?.active !== false,
      })),
    };
  });

  normalized.configuration.incident_details.types = normalizedTypes;
  return normalized;
}

const IncidentTypesAndSubTypesDialog = ({ onClose }) => {
  const { values, setValues } = useFormikContext();
  const initialValues = useMemo(() => normalizeIncidentTypesActiveDefaults(values), [values]);
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={incidentTypesAndSubTypesDialogSchema}
      onSubmit={async (values, formikProps) => {
        const { setSubmitting } = formikProps;
        try {
          setSubmitting(true);
          await setValues(values);
          onClose();
        } catch (error) {
          setSubmitting(false);
        }
      }}
    >
      <IncidentTypesAndSubTypesDialogInner onClose={onClose} />
    </Formik>
  );
};

IncidentTypesAndSubTypesDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default IncidentTypesAndSubTypesDialog;
